import {
  Building,
  FileText,
  Plus,
  Save,
  Settings,
  Trash2,
  Users,
} from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui-v2/Button";

/**
 * Type local pour le Dashboard d'édition de devis/factures
 * Ce composant utilise un format simplifié différent du modèle Prisma
 * Les templates convertissent les données via convertToQuoteDataForDashboard()
 */
interface DashboardItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

interface DashboardCompany {
  legalName: string;
  registrationNumber?: string;
  addressLine1?: string;
  zipCode?: string;
  city?: string;
  countryCode?: string;
  phone?: string;
  email?: string;
}

interface DashboardClient {
  fullName?: string;
  companyName?: string;
  addressLine1?: string;
  zipCode?: string;
  city?: string;
  countryCode?: string;
  phone?: string;
  email?: string;
  vatNumber?: string;
}

interface DashboardQuoteData {
  number: string;
  issueDate: string;
  validUntil: string;
  internalNotes?: string;
  termsConditions?: string;
  items: DashboardItem[];
  company: DashboardCompany;
  client: DashboardClient;
}

interface DashboardProps {
  quoteData: DashboardQuoteData;
  onUpdateQuote: (data: DashboardQuoteData) => void;
  onSave: () => void;
}

export const Dashboard: React.FC<DashboardProps> = ({
  quoteData,
  onUpdateQuote,
  onSave,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<
    "company" | "client" | "items" | "settings"
  >("company");

  const updateField = (path: string, value: string) => {
    const newData = { ...quoteData };
    const keys = path.split(".");
    let current: any = newData;

    // ✅ Cloner les objets imbriqués pour que React détecte les changements
    for (let i = 0; i < keys.length - 1; i++) {
      current[keys[i]] = { ...current[keys[i]] }; // Clone l'objet imbriqué
      current = current[keys[i]];
    }

    current[keys[keys.length - 1]] = value;
    onUpdateQuote(newData);
  };

  const updateItem = (id: string, field: string, value: string | number) => {
    const updatedItems = quoteData.items.map((item) => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        if (field === "quantity" || field === "unitPrice") {
          updatedItem.total = updatedItem.quantity * updatedItem.unitPrice;
        }
        return updatedItem;
      }
      return item;
    });

    onUpdateQuote({ ...quoteData, items: updatedItems });
  };

  const addItem = () => {
    const newItem = {
      id: Date.now().toString(),
      description: t('dashboard.fields.newService'),
      quantity: 1,
      unitPrice: 0,
      total: 0,
    };
    onUpdateQuote({ ...quoteData, items: [...quoteData.items, newItem] });
  };

  const removeItem = (id: string) => {
    onUpdateQuote({
      ...quoteData,
      items: quoteData.items.filter((item) => item.id !== id),
    });
  };

  const tabs = [
    { id: "company", label: t('dashboard.tabs.company'), icon: Building },
    { id: "client", label: t('dashboard.tabs.client'), icon: Users },
    { id: "items", label: t('dashboard.tabs.items'), icon: FileText },
    { id: "settings", label: t('dashboard.tabs.settings'), icon: Settings },
  ];

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          {t('dashboard.managementDashboard')}
        </h2>
        <Button
          variant="primary"
          onClick={onSave}
          icon={<Save className="w-4 h-4" />}
        >
          {t('common.actions.save')}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto border-b border-gray-200 mb-6 -mx-2 px-2 md:mx-0 md:px-0">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          // ✅ TEAL/LIME THEME - Tab buttons
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-3 md:px-4 py-2 border-b-2 transition-colors whitespace-nowrap text-sm md:text-base ${
                activeTab === tab.id
                  ? "border-teal-600 text-teal-600"
                  : "border-transparent text-gray-600 hover:text-gray-800"
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === "company" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.companyName')}
              </label>
              <input
                type="text"
                value={quoteData.company.legalName}
                onChange={(e) => updateField("company.legalName", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.siret')}
              </label>
              <input
                type="text"
                value={quoteData.company.registrationNumber}
                onChange={(e) => updateField("company.registrationNumber", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.address')}
              </label>
              <input
                type="text"
                value={quoteData.company.addressLine1}
                onChange={(e) => updateField("company.addressLine1", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.zipCode')}
              </label>
              <input
                type="text"
                value={quoteData.company.zipCode || ''}
                onChange={(e) => updateField("company.zipCode", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.city')}
              </label>
              <input
                type="text"
                value={quoteData.company.city}
                onChange={(e) => updateField("company.city", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.country')}
              </label>
              <input
                type="text"
                value={quoteData.company.countryCode || 'FR'}
                onChange={(e) => updateField("company.countryCode", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.phone')}
              </label>
              <input
                type="text"
                value={quoteData.company.phone}
                onChange={(e) => updateField("company.phone", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.email')}
              </label>
              <input
                type="email"
                value={quoteData.company.email}
                onChange={(e) => updateField("company.email", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {activeTab === "client" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.clientName')}
              </label>
              <input
                type="text"
                value={quoteData.client.fullName}
                onChange={(e) => updateField("client.fullName", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.clientCompany')}
              </label>
              <input
                type="text"
                value={quoteData.client.companyName || ""}
                onChange={(e) => updateField("client.companyName", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.address')}
              </label>
              <input
                type="text"
                value={quoteData.client.addressLine1}
                onChange={(e) => updateField("client.addressLine1", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.zipCode')}
              </label>
              <input
                type="text"
                value={quoteData.client.zipCode || ''}
                onChange={(e) => updateField("client.zipCode", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.city')}
              </label>
              <input
                type="text"
                value={quoteData.client.city}
                onChange={(e) => updateField("client.city", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.country')}
              </label>
              <input
                type="text"
                value={quoteData.client.countryCode || 'FR'}
                onChange={(e) => updateField("client.countryCode", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.phone')}
              </label>
              <input
                type="text"
                value={quoteData.client.phone}
                onChange={(e) => updateField("client.phone", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.email')}
              </label>
              <input
                type="email"
                value={quoteData.client.email}
                onChange={(e) => updateField("client.email", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.vatIntra')}
              </label>
              <input
                type="text"
                value={quoteData.client.vatNumber || ''}
                onChange={(e) => updateField("client.vatNumber", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {activeTab === "items" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {t('dashboard.tabs.items')}
              </h3>
              <Button
                variant="primary"
                onClick={addItem}
                icon={<Plus className="w-4 h-4" />}
              >
                {t('common.actions.add')}
              </Button>
            </div>

            <div className="space-y-4">
              {quoteData.items.map((item, index) => (
                <div key={item.id} className="bg-gray-50 p-3 md:p-4 rounded-lg">
                  <div className="flex flex-col md:grid md:grid-cols-12 gap-3 md:gap-4 md:items-center">
                    <div className="md:col-span-5">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('dashboard.fields.description')}
                      </label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) =>
                          updateItem(item.id, "description", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3 md:col-span-4 md:flex md:gap-4">
                      <div className="md:flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('dashboard.fields.quantity')}
                        </label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) =>
                            updateItem(
                              item.id,
                              "quantity",
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                          min="0"
                        />
                      </div>
                      <div className="md:flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('dashboard.fields.unitPrice')}
                        </label>
                        <input
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) =>
                            updateItem(
                              item.id,
                              "unitPrice",
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                    <div className="flex items-end gap-3 md:col-span-3">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('dashboard.fields.total')}
                        </label>
                        <div className="px-3 py-2 bg-gray-100 rounded-lg font-semibold text-sm">
                          {item.total.toLocaleString("fr-FR", {
                            minimumFractionDigits: 2,
                          })}
                        </div>
                      </div>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg"
                        title={t('common.actions.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "settings" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.quoteNumber')}
              </label>
              <input
                type="text"
                value={quoteData.number}
                onChange={(e) => updateField("number", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.date')}
              </label>
              <input
                type="date"
                value={quoteData.issueDate}
                onChange={(e) => updateField("issueDate", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.validUntil')}
              </label>
              <input
                type="date"
                value={quoteData.validUntil}
                onChange={(e) => updateField("validUntil", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.notes')}
              </label>
              <textarea
                value={quoteData.internalNotes}
                onChange={(e) => updateField("internalNotes", e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.fields.termsAndConditions')}
              </label>
              <textarea
                value={quoteData.termsConditions}
                onChange={(e) => updateField("termsConditions", e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
